import React, { useState, useEffect, useCallback } from 'react';
import { useTranslations } from "../i18n/LanguageProvider";

import { GitBranch, X } from 'lucide-react';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { useSelector } from 'react-redux';
import { RootState } from '../state/store';
import axios from 'axios';
import { money } from '../utilities/currency';



// ============================================================ 

// I18N — UI text 

// ============================================================ 

const ui = {

    no: {
        metaTitle: "Marketplace brukerflyt",
        metaSub: "Konsept-spesifikasjon · Versjon 1.0",
        pageTitle: "Marketplace brukerflyt",

        pageSubtitle: "Fra søk til fullført konsultasjon — fem faser",

        hint: "Klikk på et steg for detaljer · ESC eller klikk utenfor for å lukke",

        legend: ["Discovery", "Utforsking", "Booking", "Verifisering", "Gjennomføring", "Compliance", "Regnskap"],

        phase1: "FASE 1 — DISCOVERY",

        phase2: "FASE 2 — UTFORSKING AV TILBYDER",

        phase3: "FASE 3 — BOOKING OG PREAUTORISERT BETALING",

        phase4: "FASE 4 — GJENNOMFØRING OG OPPGJØR",

        phase5: "FASE 5 — DATADELING OG ANSVAR",

        closeAria: "Lukk",

        footerLeft: "Chamcore Technologies — Marketplace brukerflyt",

        footerRight: "Versjon 1.0",
        branchLabel: "Tre veier videre",
        splitLabel: "Splitter til to spor",
        mergeLabel: "Møtes igjen",

    },

    en: {

        metaTitle: "Marketplace user flow",

        metaSub: "Concept specification · Version 1.0",

        pageTitle: "Marketplace user flow",

        pageSubtitle: "From search to completed consultation — five phases",

        hint: "Click a step for details · ESC or click outside to close",

        legend: ["Discovery", "Exploration", "Booking", "Verification", "Delivery", "Compliance", "Accounting"],

        phase1: "PHASE 1 — DISCOVERY",

        phase2: "PHASE 2 — PROVIDER EXPLORATION",

        phase3: "PHASE 3 — BOOKING AND PRE-AUTHORIZED PAYMENT",

        phase4: "PHASE 4 — DELIVERY AND SETTLEMENT",

        phase5: "PHASE 5 — DATA SHARING AND RESPONSIBILITY",

        closeAria: "Close",

        footerLeft: "Chamcore Technologies — Marketplace user flow",

        footerRight: "Version 1.0",
        branchLabel: "Three paths forward",
        splitLabel: "Splits into two tracks",
        mergeLabel: "Merge again",

    }

};

const stepColors = {
    landing: { bg: '#F1EFE8', border: '#5F5E5A', text: '#2C2C2A', sub: '#444441', accent: '#5F5E5A' },
    search: { bg: '#E6F1FB', border: '#185FA5', text: '#0C447C', sub: '#185FA5', accent: '#185FA5' },
    results: { bg: '#E6F1FB', border: '#185FA5', text: '#0C447C', sub: '#185FA5', accent: '#185FA5' },
    company: { bg: '#EEEDFE', border: '#534AB7', text: '#3C3489', sub: '#534AB7', accent: '#534AB7' },
    agents: { bg: '#EEEDFE', border: '#534AB7', text: '#3C3489', sub: '#534AB7', accent: '#534AB7' },
    blog: { bg: '#EEEDFE', border: '#534AB7', text: '#3C3489', sub: '#534AB7', accent: '#534AB7' },
    bookingStart: { bg: '#EEEDFE', border: '#534AB7', text: '#3C3489', sub: '#534AB7', accent: '#534AB7' },
    slot: { bg: '#E1F5EE', border: '#0F6E56', text: '#085041', sub: '#0F6E56', accent: '#0F6E56' },
    terms: { bg: '#E1F5EE', border: '#0F6E56', text: '#085041', sub: '#0F6E56', accent: '#0F6E56' },
    preauth: { bg: '#E1F5EE', border: '#0F6E56', text: '#085041', sub: '#0F6E56', accent: '#0F6E56' },
    email: { bg: '#E1F5EE', border: '#0F6E56', text: '#085041', sub: '#0F6E56', accent: '#0F6E56' },
    account: { bg: '#FAEEDA', border: '#854F0B', text: '#633806', sub: '#854F0B', accent: '#854F0B' },
    bankid: { bg: '#FAEEDA', border: '#854F0B', text: '#633806', sub: '#854F0B', accent: '#854F0B' },
    consultation: { bg: '#FAECE7', border: '#993C1D', text: '#712B13', sub: '#993C1D', accent: '#993C1D' },
    capture: { bg: '#FAECE7', border: '#993C1D', text: '#712B13', sub: '#993C1D', accent: '#993C1D' },
    receipt: { bg: '#FAECE7', border: '#993C1D', text: '#712B13', sub: '#993C1D', accent: '#993C1D' },
    platform: { bg: '#F1EFE8', border: '#5F5E5A', text: '#2C2C2A', sub: '#444441', accent: '#5F5E5A' },
    psp: { bg: '#FBEAF0', border: '#993556', text: '#72243E', sub: '#993556', accent: '#993556' },
    dashboard: { bg: '#E6F1FB', border: '#185FA5', text: '#0C447C', sub: '#185FA5', accent: '#185FA5' },
    accounting: { bg: '#EAF3DE', border: '#3B6D11', text: '#27500A', sub: '#3B6D11', accent: '#3B6D11' },
};

const flowStructure = [
    { type: 'phase', key: 'phase1', color: '#D3D1C7' },
    { type: 'step', step: 'landing' },
    { type: 'arrow' },
    { type: 'step', step: 'search' },
    { type: 'arrow' },
    { type: 'step', step: 'results' },
    { type: 'arrow' },
    { type: 'phase', key: 'phase2', color: '#CECBF6' },
    { type: 'step', step: 'company' },
    { type: 'branchLabel' },
    { type: 'branch', steps: ['agents', 'blog', 'bookingStart'] },
    { type: 'arrow' },
    { type: 'phase', key: 'phase3', color: '#9FE1CB' },
    { type: 'step', step: 'slot' },
    { type: 'arrow' },
    { type: 'step', step: 'terms' },
    { type: 'arrow' },
    { type: 'step', step: 'preauth' },
    { type: 'splitLabel' },
    { type: 'parallel', steps: ['email', 'account'] },
    { type: 'arrow' },
    { type: 'step', step: 'bankid' },
    { type: 'arrow' },
    { type: 'phase', key: 'phase4', color: '#F5C4B3' },
    { type: 'step', step: 'consultation' },
    { type: 'arrow' },
    { type: 'step', step: 'capture' },
    { type: 'arrow' },
    { type: 'step', step: 'receipt' },
    { type: 'arrow' },
    { type: 'phase', key: 'phase5', color: '#F4C0D1' },
    { type: 'step', step: 'platform' },
    { type: 'splitLabel' },
    { type: 'parallel', steps: ['psp', 'dashboard'] },
    { type: 'mergeLabel' },
    { type: 'step', step: 'accounting' },
];


// Node labels per language 

const nodeLabels = {

    no: {

        landing: ["Landingsside", "Marketplace forside"],

        search: ["Søk", "Tjeneste, bedrift, agent"],

        results: ["Treff", "Resultatliste"],

        company: ["Bedriftsside", "Profil og tjenester"],

        agents: ["Agentprofiler", "Personlig vurdering"],

        blog: ["IVR Telefoni", "Stemmestyrt kanal"],

        bookingStart: ["Booking", "Direkte handling"],

        slot: ["Velg tid", "Tjeneste og slot"],

        terms: ["Vilkår vises", "Aksept før betaling"],

        preauth: ["Preauth via PSP", "Reservasjon på kort"],

        email: ["E-postbekreftelse", "Booking og kvittering"],

        account: ["Brukerkonto opprettes", "Klar for verifisering"],

        bankid: ["BankID / Vipps", "Identitet bekreftet"],

        consultation: ["Konsultasjon", "Møte gjennomført"],

        capture: ["Capture via PSP", "Trekk eller frigi etter utfall"],

        receipt: ["Kvittering", "Tilgjengelig i dashboard"],

        platform: ["Plattform", "Lagrer kun brukerinfo"],

        psp: ["PSP håndterer compliance", "KYC, AML, regnskapsdata", "Ingen integrasjon nødvendig"],

        dashboard: ["Bruker dashboard", "Bestillinger og kvitteringer", "Vilkår alltid tilgjengelig"],

        accounting: ["Bedrift / agent henter regnskap", "Direkte fra PSP — ikke fra plattform"],

    },

    en: {

        landing: ["Landing page", "Marketplace homepage"],

        search: ["Search", "Service, company, agent"],

        results: ["Results", "Search result list"],

        company: ["Company page", "Profile and services"],

        agents: ["Agent profiles", "Personal assessment"],
        blog: ["IVR phone", "Voice-driven channel"],
        bookingStart: ["Booking", "Direct action"],

        slot: ["Select time", "Service and slot"],

        terms: ["Terms shown", "Accept before payment"],

        preauth: ["Preauth via PSP", "Card reservation"],

        email: ["Email confirmation", "Booking and receipt"],

        account: ["User account created", "Ready for verification"],

        bankid: ["BankID / Vipps", "Identity verified"],

        consultation: ["Consultation", "Meeting completed"],

        capture: ["Capture via PSP", "Capture or release by outcome"],

        receipt: ["Receipt", "Available in dashboard"],

        platform: ["Platform", "Stores user info only"],

        psp: ["PSP handles compliance", "KYC, AML, accounting data", "No integration required"],

        dashboard: ["User dashboard", "Bookings and receipts", "Terms always accessible"],

        accounting: ["Company / agent retrieves accounting", "Directly from PSP — not the platform"],

    }

};



// ============================================================ 

// STEP DATA — popup content 

// ============================================================ 

const stepData = {

    landing: {

        no: {

            tag: "Fase 1 · Discovery", tagClass: "tag-discovery",

            title: "Landingsside", subtitle: "Inngangsporten til marketplace",

            tooltip: "Marketplace forside — første kontaktpunkt for brukeren",

            body: (

                <>

                    <p>Brukerreisen begynner her. Landingssiden fungerer som inngangsport til hele økosystemet og er optimalisert for én ting: å hjelpe brukeren finne den tjenesten, bedriften eller agenten de leter etter.</p>

                    <p>Designprinsippet er minimalt med distraksjoner — søkefeltet står sentralt, med kategorier og fremhevede tilbydere som sekundær navigasjon.</p>

                    <ul className="detail-list">

                        <li>Sentralt plassert søkefelt</li>

                        <li>Kategorinavigasjon for utforsking</li>

                        <li>Fremhevede tilbydere og nyheter</li>

                        <li>Rask lasting og responsivt design</li>

                    </ul>

                    <MetaRow items={[["Aktør", "Bruker"], ["Krav", "Ingen innlogging"]]} />

                </>

            )

        },

        en: {

            tag: "Phase 1 · Discovery", tagClass: "tag-discovery",

            title: "Landing page", subtitle: "The marketplace entry point",

            tooltip: "Marketplace homepage — the user's first touchpoint",

            body: (

                <>

                    <p>The user journey starts here. The landing page acts as the gateway to the entire ecosystem and is optimized for one thing: helping the user find the service, company, or agent they are looking for.</p>

                    <p>The design principle is minimal distraction — the search field is central, with categories and featured providers as secondary navigation.</p>

                    <ul className="detail-list">

                        <li>Centrally placed search field</li>

                        <li>Category navigation for exploration</li>

                        <li>Featured providers and news</li>

                        <li>Fast loading and responsive design</li>

                    </ul>

                    <MetaRow items={[["Actor", "User"], ["Requirement", "No login required"]]} />

                </>

            )

        }

    },

    search: {

        no: {

            tag: "Fase 1 · Discovery", tagClass: "tag-discovery",

            title: "Søk", subtitle: "Tre primære søkeentiteter",

            tooltip: "Fritekstsøk på tjeneste, bedrift eller agent",

            body: (

                <>

                    <p>Brukeren kan søke fritekst etter tre primære entiteter. Søkemotoren returnerer sammensatte resultater på tvers av disse, vektet etter relevans og brukerens lokasjon.</p>

                    <ul className="detail-list">

                        <li><strong>Tjeneste</strong> — eksempelvis "konsultasjon", "rådgivning", "vurdering"</li>

                        <li><strong>Bedrift</strong> — registrerte tilbydere på plattformen</li>

                        <li><strong>Agent</strong> — enkeltpersoner tilknyttet en bedrift</li>

                    </ul>

                    <p>Filtre lar brukeren snevre inn på pris, tilgjengelighet, geografi og spesialisering.</p>

                </>

            )

        },

        en: {

            tag: "Phase 1 · Discovery", tagClass: "tag-discovery",

            title: "Search", subtitle: "Three primary search entities",

            tooltip: "Free-text search across service, company, or agent",

            body: (

                <>

                    <p>The user can search free-text across three primary entities. The search engine returns combined results across all three, weighted by relevance and user location.</p>

                    <ul className="detail-list">

                        <li><strong>Service</strong> — e.g. "consultation", "advisory", "assessment"</li>

                        <li><strong>Company</strong> — registered providers on the platform</li>

                        <li><strong>Agent</strong> — individuals affiliated with a company</li>

                    </ul>

                    <p>Filters let the user narrow by price, availability, geography, and specialization.</p>

                </>

            )

        }

    },

    results: {

        no: {

            tag: "Fase 1 · Discovery", tagClass: "tag-discovery",

            title: "Søkeresultater", subtitle: "Resultatlisten med metadata",

            tooltip: "Treff vises med nok info til å vurdere relevans",

            body: (

                <>

                    <p>Resultatlisten viser hver oppføring med tilstrekkelig metadata til at brukeren kan vurdere relevans før de klikker videre — uten å måtte besøke hver enkelt profil.</p>

                    <ul className="detail-list">

                        <li>Navn, kategori og kort beskrivelse</li>

                        <li>Stjerner og antall vurderinger</li>

                        <li>Prisindikasjon og tilgjengelighet</li>

                        <li>Avstand eller geografisk område</li>

                    </ul>

                </>

            )

        },

        en: {

            tag: "Phase 1 · Discovery", tagClass: "tag-discovery",

            title: "Search results", subtitle: "Result list with metadata",

            tooltip: "Results shown with enough info to assess relevance",

            body: (

                <>

                    <p>The result list shows each entry with sufficient metadata for the user to assess relevance before clicking through.</p>

                    <ul className="detail-list">

                        <li>Name, category, and short description</li>

                        <li>Star rating and number of reviews</li>

                        <li>Price indication and availability</li>

                        <li>Distance or geographic area</li>

                    </ul>

                </>

            )

        }

    },

    company: {

        no: {

            tag: "Fase 2 · Utforsking", tagClass: "tag-utforsking",

            title: "Bedriftsside", subtitle: "Tilbyderens hovedprofil",

            tooltip: "Navet for tilbyderen — gir tre handlingsalternativer",

            body: (

                <>

                    <p>Bedriftssiden er navet for tilbyderen og gir brukeren full kontekst før de forplikter seg økonomisk. Her samles alt en bruker trenger for å ta en informert beslutning.</p>

                    <p>Siden er strukturert rundt tre primære handlinger som dekker forskjellige kanaler og brukerpreferanser: rask vurdering (agentprofiler), telefonisk kontakt (IVR), og direkte handling (booking).</p>

                    <ul className="detail-list">

                        <li>Bedriftsbeskrivelse og kjerneverdier</li>

                        <li>Liste over agenter med spesialiseringer</li>

                        <li>Tjenestetilbud og priser</li>

                        <li>Kundeomtaler og referanser</li>

                    </ul>

                </>

            )

        },

        en: {

            tag: "Phase 2 · Exploration", tagClass: "tag-utforsking",

            title: "Company page", subtitle: "The provider's main profile",

            tooltip: "The provider's hub — offers three action paths",

            body: (

                <>

                    <p>The company page is the provider's hub and gives the user full context before any financial commitment. Everything needed for an informed decision is consolidated here.</p>

                    <p>The page is structured around three primary actions covering different channels and user preferences: quick assessment (agent profiles), phone contact (IVR), and direct action (booking).</p>

                    <ul className="detail-list">

                        <li>Company description and core values</li>

                        <li>List of agents with specializations</li>

                        <li>Service offerings and pricing</li>

                        <li>Customer reviews and references</li>

                    </ul>

                </>

            )

        }

    },

    agents: {

        no: {

            tag: "Fase 2 · Utforsking", tagClass: "tag-utforsking",

            title: "Agentprofiler", subtitle: "Enkeltpersoner tilknyttet bedriften",

            tooltip: "Kvalifikasjoner, spesialfelt og tilgjengelighet",

            body: (

                <>

                    <p>Agentprofiler lar brukeren vurdere den konkrete personen de potensielt vil møte. Dette skaper tillit på et personlig nivå før booking.</p>

                    <ul className="detail-list">

                        <li>Bilde og faglig bakgrunn</li>

                        <li>Spesialfelt og kompetanseområder</li>

                        <li>Tilgjengelighet og responstid</li>

                        <li>Språk og kommunikasjonspreferanser</li>

                    </ul>

                </>

            )

        },

        en: {

            tag: "Phase 2 · Exploration", tagClass: "tag-utforsking",

            title: "Agent profiles", subtitle: "Individuals affiliated with the company",

            tooltip: "Qualifications, specializations, and availability",

            body: (

                <>

                    <p>Agent profiles let the user evaluate the specific person they may meet. This creates trust at a personal level before booking.</p>

                    <ul className="detail-list">

                        <li>Photo and professional background</li>

                        <li>Specializations and areas of expertise</li>

                        <li>Availability and response time</li>

                        <li>Languages and communication preferences</li>

                    </ul>

                </>

            )

        }

    },

    blog: {

        no: {

            tag: "Fase 2 · Utforsking", tagClass: "tag-utforsking",

            title: "IVR Telefoni", subtitle: "Stemmestyrt tilgang med full betalings- og kundeflyt",

            tooltip: "Telefonkanal med samme betalingsflyt og kundeidentifikasjon som web",

            body: (

                <>

                    <p>IVR (Interactive Voice Response) er et stemmestyrt telefonisystem som lar brukere navigere tilbyderens tjenester uten å gå via nettsiden. Dette gir et alternativt og inkluderende inngangspunkt — særlig viktig for brukere som foretrekker telefon, eller for tjenester der øyeblikkelig kontakt er kritisk.</p>

                    <p>Brukeren ringer et nummer tilknyttet bedriften og navigerer en menystruktur for å finne riktig agent eller tjeneste. Systemet kan rute videre til booking, direkte samtale, eller tilbakeringing avhengig av tilgjengelighet.</p>



                    <h3>Kundeidentifikasjon ved oppringing</h3>

                    <p>IVR identifiserer automatisk om innringer er en registrert kunde basert på nummeret de ringer fra. Flyten splittes deretter i to spor:</p>



                    <UnregisteredFlow lang="no" />

                    <RegisteredFlow lang="no" />



                    <h3>Vipps push-registrering</h3>

                    <p>For uregistrerte innringere sender IVR en Vipps-forespørsel direkte til mobilen mens samtalen pågår. Brukeren bekrefter med biometri eller PIN, og kontoen opprettes og verifiseres umiddelbart — samme nivå som BankID/Vipps-flyten i fase 3. Innringer trenger ikke legge på.</p>

                    <p><strong>Kombinert registrering og preauth i ett trykk:</strong> samme Vipps-push kan inkludere preauth-bekreftelse for tjenesten innringer ringer for å booke. Med én biometrisk bekreftelse oppnår brukeren tre ting samtidig: kontoopprettelse, identitetsverifisering og preautorisert betaling. Dette eliminerer alle tradisjonelle friksjonspunkter ved telefonbestilling — fra anonym innringer til betalende verifisert kunde på sekunder, uten å legge på.</p>



                    <h3>Betalingsintegrasjon</h3>

                    <p>IVR er koblet direkte mot samme betalingsflyt som web. Innringer belastes via PSP med identisk preauth → capture-mekanikk som ved nettbestilling — full release ved kansellering, full eller delvis capture etter konsultasjon, full capture ved no-show. Dette sikrer en konsistent finansiell og regulatorisk håndtering på tvers av kanaler.</p>



                    <ul className="detail-list">

                        <li>Menystyrt navigasjon med tastetrykk eller tale</li>

                        <li>Automatisk gjenkjenning av registrert/uregistrert innringer</li>

                        <li>Vipps push — registrering og preauth i samme bekreftelse</li>

                        <li>Preauth og capture via samme PSP som web</li>

                        <li>Køhåndtering og estimert ventetid</li>

                        <li>Tilbakeringing utenfor åpningstid</li>

                        <li>Full release of hold ved manglende svar fra agent</li>

                        <li>Samtalelogg og kvittering tilgjengelig i dashboard</li>

                    </ul>

                    <MetaRow items={[["Kanal", "Telefon"], ["Betaling", "PSP — samme som web"], ["Registrering", "Vipps push"]]} />

                </>

            )

        },

        en: {

            tag: "Phase 2 · Exploration", tagClass: "tag-utforsking",

            title: "IVR phone", subtitle: "Voice-driven access with full payment and customer flow",

            tooltip: "Phone channel with the same payment flow and customer identification as web",

            body: (

                <>

                    <p>IVR (Interactive Voice Response) is a voice-driven phone system that lets users navigate the provider's services without going through the website. It provides an alternative, inclusive entry point — particularly important for users who prefer phone, or for services where immediate contact is critical.</p>

                    <p>The user calls a number assigned to the company and navigates a menu structure to reach the right agent or service. The system can route to booking, a live call, or a callback depending on availability.</p>



                    <h3>Caller identification</h3>

                    <p>IVR automatically identifies whether the caller is a registered customer based on the number they're calling from. The flow then splits into two tracks:</p>



                    <UnregisteredFlow lang="en" />

                    <RegisteredFlow lang="en" />



                    <h3>Vipps push registration</h3>

                    <p>For unregistered callers, the IVR sends a Vipps request directly to the mobile while the call is still active. The user confirms with biometrics or PIN, and the account is created and verified immediately — at the same level as the BankID/Vipps flow in phase 3. The caller does not need to hang up.</p>

                    <p><strong>Combined registration and preauth in one tap:</strong> the same Vipps push can include preauth confirmation for the service the caller is booking. With a single biometric confirmation, the user achieves three things at once: account creation, identity verification, and pre-authorized payment. This eliminates every traditional friction point in phone booking — from anonymous caller to paying verified customer in seconds, without hanging up.</p>



                    <h3>Payment integration</h3>

                    <p>IVR is connected directly to the same payment flow as web. The caller is charged via PSP with the identical preauth → capture mechanics as web bookings — full release on cancellation, full or partial capture after consultation, full capture on no-show. This ensures consistent financial and regulatory handling across channels.</p>



                    <ul className="detail-list">

                        <li>Menu navigation by keypad or voice</li>

                        <li>Automatic recognition of registered vs. unregistered callers</li>

                        <li>Vipps push — registration and preauth in one confirmation</li>

                        <li>Preauth and capture via the same PSP as web</li>

                        <li>Queue handling and estimated wait time</li>

                        <li>Callback outside business hours</li>

                        <li>Full release of hold if the agent doesn't answer</li>

                        <li>Call log and receipt available in the dashboard</li>

                    </ul>

                    <MetaRow items={[["Channel", "Phone"], ["Payment", "PSP — same as web"], ["Registration", "Vipps push"]]} />

                </>

            )

        }

    },

    bookingStart: {

        no: {

            tag: "Fase 2 · Utforsking", tagClass: "tag-utforsking",

            title: "Start booking", subtitle: "Overgang til betalingsflyt",

            tooltip: "Innledning til booking — leder over i fase 3",

            body: (

                <>

                    <p>Når brukeren har valgt å booke, starter den strukturerte betalingsflyten. Dette er overgangen fra utforsking til forpliktelse.</p>

                    <p>Bookingknappen er synlig på bedriftssiden, agentprofiler og som menyvalg i IVR-systemet — slik at konverteringspunktet alltid er nært uansett hvor i utforskingsreisen brukeren befinner seg.</p>

                </>

            )

        },

        en: {

            tag: "Phase 2 · Exploration", tagClass: "tag-utforsking",

            title: "Start booking", subtitle: "Transition into the payment flow",

            tooltip: "Booking entry — leads into phase 3",

            body: (

                <>

                    <p>When the user chooses to book, the structured payment flow begins. This is the transition from exploration to commitment.</p>

                    <p>The booking button is visible on the company page, agent profiles, and as a menu option in the IVR system — so the conversion point is always nearby regardless of where the user is in their exploration journey.</p>

                </>

            )

        }

    },

    slot: {

        no: {

            tag: "Fase 3 · Booking", tagClass: "tag-booking",

            title: "Velg tid", subtitle: "Kalenderintegrasjon med tilgjengelige slots",

            tooltip: "Brukeren velger tjeneste og tidspunkt",

            body: (

                <>

                    <p>Brukeren velger ønsket tjeneste og tidsslot fra agentens kalender. Tilgjengelige slots oppdateres i sanntid for å unngå dobbeltbooking.</p>

                    <ul className="detail-list">

                        <li>Sanntidskalender per agent</li>

                        <li>Tjenestespesifikk varighet</li>

                        <li>Tidssone-håndtering</li>

                        <li>Mulighet for tilleggstjenester</li>

                    </ul>

                </>

            )

        },

        en: {

            tag: "Phase 3 · Booking", tagClass: "tag-booking",

            title: "Select time", subtitle: "Calendar integration with available slots",

            tooltip: "User selects service and time",

            body: (

                <>

                    <p>The user selects the desired service and time slot from the agent's calendar. Available slots refresh in real time to prevent double-booking.</p>

                    <ul className="detail-list">

                        <li>Real-time calendar per agent</li>

                        <li>Service-specific duration</li>

                        <li>Time zone handling</li>

                        <li>Option for add-on services</li>

                    </ul>

                </>

            )

        }

    },

    terms: {

        no: {

            tag: "Fase 3 · Booking", tagClass: "tag-booking",

            title: "Vilkår vises", subtitle: "Aktiv aksept før betaling",

            tooltip: "Vilkår må aksepteres aktivt før preauth",

            body: (

                <>

                    <p>Før betalingen initieres, presenteres vilkårene tydelig. Dette inkluderer kanselleringsfrister, refusjonspolicy, og hva som skjer ved manglende oppmøte.</p>

                    <p>Vilkår er alltid tilgjengelige tre steder for å sikre transparens:</p>

                    <ul className="detail-list">

                        <li>I selve bookingbekreftelsen</li>

                        <li>På bedriftens marketplace-side</li>

                        <li>Under "Terms and conditions" i brukerprofilen</li>

                    </ul>

                    <MetaRow items={[["Krav", "Aktiv avkrysning"], ["Logg", "Tidsstemplet"]]} />

                </>

            )

        },

        en: {

            tag: "Phase 3 · Booking", tagClass: "tag-booking",

            title: "Terms shown", subtitle: "Active acceptance required before payment",

            tooltip: "Terms must be actively accepted before preauth",

            body: (

                <>

                    <p>Before payment is initiated, the terms are presented clearly. This includes cancellation deadlines, refund policy, and consequences of no-shows.</p>

                    <p>Terms are always available in three places to ensure transparency:</p>

                    <ul className="detail-list">

                        <li>In the booking confirmation itself</li>

                        <li>On the provider's marketplace page</li>

                        <li>Under "Terms and conditions" in the user profile</li>

                    </ul>

                    <MetaRow items={[["Requirement", "Active checkbox"], ["Log", "Timestamped"]]} />

                </>

            )

        }

    },

    preauth: {

        no: {

            tag: "Fase 3 · Booking", tagClass: "tag-booking",

            title: "Preauth via PSP", subtitle: "Reservasjon på kort — ikke trekk",

            tooltip: "Beløpet reserveres men trekkes ikke ennå",

            body: (

                <>

                    <p>Preautorisering kjøres gjennom betalingstjenesteleverandøren (PSP). Beløpet reserveres på brukerens kort, men trekkes ikke ennå. Dette beskytter både bruker og tilbyder:</p>

                    <ul className="detail-list">

                        <li>Bruker betaler ikke før tjenesten er levert</li>

                        <li>Tilbyder har sikkerhet for at midler er tilgjengelige</li>

                        <li>Reservasjonen utløper etter avtalt periode hvis ikke captured</li>

                        <li>Refusjon er enkel ved kansellering</li>

                    </ul>

                    <MetaRow items={[["Aktør", "PSP"], ["Plattform", "Initierer kun"]]} />

                </>

            )

        },

        en: {

            tag: "Phase 3 · Booking", tagClass: "tag-booking",

            title: "Preauth via PSP", subtitle: "Card reservation — not yet captured",

            tooltip: "Amount is reserved but not yet charged",

            body: (

                <>

                    <p>Pre-authorization runs through the payment service provider (PSP). The amount is reserved on the user's card but not yet charged. This protects both user and provider:</p>

                    <ul className="detail-list">

                        <li>User doesn't pay until the service is delivered</li>

                        <li>Provider has assurance funds are available</li>

                        <li>Reservation expires after the agreed period if not captured</li>

                        <li>Refunds are simple on cancellation</li>

                    </ul>

                    <MetaRow items={[["Actor", "PSP"], ["Platform", "Only initiates"]]} />

                </>

            )

        }

    },

    email: {

        no: {

            tag: "Fase 3 · Booking", tagClass: "tag-booking",

            title: "E-postbekreftelse", subtitle: "Booking-detaljer og foreløpig kvittering",

            tooltip: "Sendes umiddelbart etter preauth",

            body: (

                <>

                    <p>Bruker mottar e-postbekreftelse umiddelbart etter vellykket preauth. E-posten fungerer som første offisielle dokumentasjon på bookingen.</p>

                    <ul className="detail-list">

                        <li>Booking-ID og tidspunkt</li>

                        <li>Tilbyder og agent</li>

                        <li>Foreløpig kvittering med reservert beløp</li>

                        <li>Lenker til vilkår og kanselleringsmuligheter</li>

                        <li>Kalenderinvitasjon (ICS)</li>

                    </ul>

                </>

            )

        },

        en: {

            tag: "Phase 3 · Booking", tagClass: "tag-booking",

            title: "Email confirmation", subtitle: "Booking details and provisional receipt",

            tooltip: "Sent immediately after preauth",

            body: (

                <>

                    <p>The user receives an email confirmation immediately after a successful preauth. The email serves as the first official documentation of the booking.</p>

                    <ul className="detail-list">

                        <li>Booking ID and timestamp</li>

                        <li>Provider and agent</li>

                        <li>Provisional receipt with reserved amount</li>

                        <li>Links to terms and cancellation options</li>

                        <li>Calendar invite (ICS)</li>

                    </ul>

                </>

            )

        }

    },

    account: {

        no: {

            tag: "Fase 3 · Verifisering", tagClass: "tag-verifisering",

            title: "Brukerkonto opprettes", subtitle: "Automatisk opprettelse — venter på verifisering",

            tooltip: "Konto opprettes parallelt med preauth",

            body: (

                <>

                    <p>Parallelt med preauth opprettes en brukerkonto automatisk basert på dataene som ble fylt inn under booking. Kontoen er aktiv men begrenset inntil identitetsverifisering er gjennomført.</p>

                    <p>Dette eliminerer friksjon — brukeren trenger ikke registrere seg som et separat steg.</p>

                    <ul className="detail-list">

                        <li>Auto-opprettet basert på booking-data</li>

                        <li>Magisk innloggingslenke i e-post</li>

                        <li>Begrenset funksjonalitet før verifisering</li>

                    </ul>

                </>

            )

        },

        en: {

            tag: "Phase 3 · Verification", tagClass: "tag-verifisering",

            title: "User account created", subtitle: "Auto-created — pending verification",

            tooltip: "Account is created in parallel with preauth",

            body: (

                <>

                    <p>In parallel with preauth, a user account is automatically created based on the data entered during booking. The account is active but limited until identity verification is completed.</p>

                    <p>This eliminates friction — the user doesn't have to register as a separate step.</p>

                    <ul className="detail-list">

                        <li>Auto-created from booking data</li>

                        <li>Magic login link by email</li>

                        <li>Limited functionality before verification</li>

                    </ul>

                </>

            )

        }

    },

    bankid: {

        no: {

            tag: "Fase 3 · Verifisering", tagClass: "tag-verifisering",

            title: "BankID / Vipps", subtitle: "Sterk identitetsbekreftelse",

            tooltip: "Verifisering må fullføres før konsultasjon",

            body: (

                <>

                    <p>Brukerkontoen må verifiseres med BankID eller Vipps før konsultasjonen kan gjennomføres. Dette sikrer sterk identitetsbekreftelse i tråd med norske standarder.</p>

                    <p>Verifiseringen knytter bookingen entydig til en juridisk identifiserbar person — kritisk for tjenester med faglig ansvar (helse, juridisk, finansiell rådgivning).</p>

                    <ul className="detail-list">

                        <li>BankID for full juridisk identifikasjon</li>

                        <li>Vipps som rask alternativ</li>

                        <li>Verifisering kreves kun én gang per konto</li>

                        <li>Tilfredsstiller KYC-krav</li>

                    </ul>

                </>

            )

        },

        en: {

            tag: "Phase 3 · Verification", tagClass: "tag-verifisering",

            title: "BankID / Vipps", subtitle: "Strong identity verification",

            tooltip: "Verification must be completed before consultation",

            body: (

                <>

                    <p>The user account must be verified with BankID or Vipps before the consultation can take place. This ensures strong identity verification consistent with Norwegian standards.</p>

                    <p>The verification ties the booking unambiguously to a legally identifiable person — critical for services with professional liability (medical, legal, financial advisory).</p>

                    <ul className="detail-list">

                        <li>BankID for full legal identification</li>

                        <li>Vipps as a fast alternative</li>

                        <li>Verification required only once per account</li>

                        <li>Satisfies KYC requirements</li>

                    </ul>

                </>

            )

        }

    },

    consultation: {

        no: {

            tag: "Fase 4 · Gjennomføring", tagClass: "tag-gjennomforing",

            title: "Konsultasjon", subtitle: "Selve tjenesteleveransen",

            tooltip: "Møtet gjennomføres som avtalt",

            body: (

                <>

                    <p>Konsultasjonen gjennomføres som avtalt. Plattformen er ikke involvert i selve leveransen — det er en sak mellom bruker og tilbyder.</p>

                    <p>Avhengig av tjenestetype kan konsultasjonen være fysisk, video, telefon, eller asynkron meldingsutveksling. Plattformen kan tilby video-rom som tjeneste, men dette er valgfritt.</p>

                    <ul className="detail-list">

                        <li>Fysisk eller digital leveranse</li>

                        <li>Plattformen er ikke part i tjenesten</li>

                        <li>Tilbyder ansvarlig for fagligheten</li>

                    </ul>

                </>

            )

        },

        en: {

            tag: "Phase 4 · Delivery", tagClass: "tag-gjennomforing",

            title: "Consultation", subtitle: "The actual service delivery",

            tooltip: "The meeting is conducted as agreed",

            body: (

                <>

                    <p>The consultation is conducted as agreed. The platform is not involved in the delivery itself — it's strictly between user and provider.</p>

                    <p>Depending on the service type, the consultation may be in-person, video, phone, or asynchronous messaging. The platform can offer video rooms as a service, but this is optional.</p>

                    <ul className="detail-list">

                        <li>Physical or digital delivery</li>

                        <li>The platform is not a party to the service</li>

                        <li>The provider is responsible for professional quality</li>

                    </ul>

                </>

            )

        }

    },

    capture: {

        no: {

            tag: "Fase 4 · Gjennomføring", tagClass: "tag-gjennomforing",

            title: "Capture via PSP", subtitle: "Trekk eller frigi reservasjonen — fire utfall",

            tooltip: "Beløpet trekkes eller frigis basert på hva som faktisk skjedde",

            body: (

                <>

                    <p>Når møtet skal gjøres opp, utløses capture-prosessen via PSP. Avhengig av hva som faktisk skjedde, ender bookingen i ett av fire definerte utfall — alle automatisk basert på vilkårene som ble akseptert under booking.</p>

                    <ul className="detail-list">

                        <li><strong>Full capture</strong> ved fullført konsultasjon — hele det reserverte beløpet trekkes</li>

                        <li><strong>Delvis capture</strong> ved kortere møte enn planlagt — kun den delen som er levert trekkes, resten frigis</li>

                        <li><strong>Full capture ved no-show</strong> — manglende oppmøte uten varsel utløser fullt trekk</li>

                        <li><strong>Full release ved kansellering</strong> — kansellering innen frist frigir hele reservasjonen</li>

                    </ul>

                    <p>Reglene defineres av tilbyder i vilkårene og er kjent for brukeren før preauth.</p>

                    <MetaRow items={[["Aktør", "PSP"], ["Plattform", "Trigger og logger"]]} />

                </>

            )

        },

        en: {

            tag: "Phase 4 · Delivery", tagClass: "tag-gjennomforing",

            title: "Capture via PSP", subtitle: "Capture or release the reservation — four outcomes",

            tooltip: "The amount is captured or released based on what actually happened",

            body: (

                <>

                    <p>When the booking is settled, the capture process is triggered via the PSP. Depending on what actually happened, the booking ends in one of four defined outcomes.</p>

                    <ul className="detail-list">

                        <li><strong>Full capture</strong> on completed consultation — the entire reserved amount is charged</li>

                        <li><strong>Partial capture</strong> for shorter sessions than planned — only the delivered portion is charged</li>

                        <li><strong>Full capture on no-show</strong> — missed appointments without notice trigger full charge</li>

                        <li><strong>Full release on cancellation</strong> — cancellations within deadline release the entire reservation</li>

                    </ul>

                    <p>The rules are defined by the provider in the terms and disclosed to the user before preauth.</p>

                    <MetaRow items={[["Actor", "PSP"], ["Platform", "Trigger and log"]]} />

                </>

            )

        }

    },

    receipt: {

        no: {

            tag: "Fase 4 · Gjennomføring", tagClass: "tag-gjennomforing",

            title: "Kvittering", subtitle: "Tilgjengelig i dashboard",

            tooltip: "Endelig kvittering opprettet av PSP",

            body: (

                <>

                    <p>Endelig kvittering blir umiddelbart tilgjengelig i brukerens dashboard. Den genereres av PSP og inneholder all informasjon som kreves for skattemessig dokumentasjon.</p>

                    <ul className="detail-list">

                        <li>Sendes også per e-post</li>

                        <li>Lastes ned som PDF</li>

                        <li>MVA spesifisert</li>

                        <li>Lenker til vilkår som ble akseptert</li>

                    </ul>

                </>

            )

        },

        en: {

            tag: "Phase 4 · Delivery", tagClass: "tag-gjennomforing",

            title: "Receipt", subtitle: "Available in the dashboard",

            tooltip: "Final receipt created by the PSP",

            body: (

                <>

                    <p>The final receipt becomes immediately available in the user's dashboard. It is generated by the PSP and contains all the information required for tax documentation.</p>

                    <ul className="detail-list">

                        <li>Also sent by email</li>

                        <li>Downloadable as PDF</li>

                        <li>VAT itemized</li>

                        <li>Links to the accepted terms</li>

                    </ul>

                </>

            )

        }

    },

    platform: {

        no: {

            tag: "Fase 5 · Compliance", tagClass: "tag-discovery",

            title: "Plattformens rolle", subtitle: "Lagrer kun brukerinfo — ikke finansdata",

            tooltip: "Klar separasjon mellom plattform og PSP",

            body: (

                <>

                    <p>Dette er arkitekturens viktigste designprinsipp. Plattformen lagrer kun det som er nødvendig for å levere brukeropplevelsen:</p>

                    <ul className="detail-list">

                        <li>Kontoinformasjon (navn, e-post, telefon)</li>

                        <li>Bookinghistorikk (referanser)</li>

                        <li>Kvitteringsreferanser (ikke selve betalingsdataene)</li>

                        <li>Preferanser og innstillinger</li>

                    </ul>

                    <p>All finansiell data, inkludert kortinformasjon, transaksjonsdetaljer og regnskapsdata, ligger hos PSP.</p>

                </>

            )

        },

        en: {

            tag: "Phase 5 · Compliance", tagClass: "tag-discovery",

            title: "The platform's role", subtitle: "Stores user info only — no financial data",

            tooltip: "Clear separation between platform and PSP",

            body: (

                <>

                    <p>This is the most important design principle of the architecture. The platform stores only what is needed to deliver the user experience:</p>

                    <ul className="detail-list">

                        <li>Account information (name, email, phone)</li>

                        <li>Booking history (references)</li>

                        <li>Receipt references (not the payment data itself)</li>

                        <li>Preferences and settings</li>

                    </ul>

                    <p>All financial data — card information, transaction details, accounting data — resides with the PSP.</p>

                </>

            )

        }

    },

    psp: {

        no: {

            tag: "Fase 5 · Compliance", tagClass: "tag-compliance",

            title: "PSP håndterer compliance", subtitle: "All finansiell compliance outsources",

            tooltip: "KYC, AML og regnskapsdata håndteres eksternt",

            body: (

                <>

                    <p>Betalingstjenesteleverandøren (PSP) håndterer all finansiell compliance. Dette er kjernen i Chamcores arkitekturvalg:</p>

                    <ul className="detail-list">

                        <li><strong>KYC</strong> — Know Your Customer-prosesser</li>

                        <li><strong>AML</strong> — Anti-Money Laundering overvåking</li>

                        <li><strong>Transaksjonsovervåking</strong> for regulatorisk rapportering</li>

                        <li><strong>Regnskapsdata</strong> for tilbyderne</li>

                        <li><strong>PCI DSS</strong> for kortdata</li>

                    </ul>

                    <p>Resultatet: ingen integrasjoner mot Tripletex, Fiken, Visma eller lignende kreves fra plattformens side.</p>

                    <MetaRow items={[["Ansvar", "PSP"], ["Plattform-eksponering", "Minimal"]]} />

                </>

            )

        },

        en: {

            tag: "Phase 5 · Compliance", tagClass: "tag-compliance",

            title: "PSP handles compliance", subtitle: "All financial compliance outsourced",

            tooltip: "KYC, AML, and accounting data handled externally",

            body: (

                <>

                    <p>The payment service provider (PSP) handles all financial compliance. This is the core of Chamcore's architectural choice:</p>

                    <ul className="detail-list">

                        <li><strong>KYC</strong> — Know Your Customer processes</li>

                        <li><strong>AML</strong> — Anti-Money Laundering monitoring</li>

                        <li><strong>Transaction monitoring</strong> for regulatory reporting</li>

                        <li><strong>Accounting data</strong> for providers</li>

                        <li><strong>PCI DSS</strong> for card data</li>

                    </ul>

                    <p>The result: no integrations with Tripletex, Fiken, Visma, or similar are required from the platform.</p>

                    <MetaRow items={[["Responsibility", "PSP"], ["Platform exposure", "Minimal"]]} />

                </>

            )

        }

    },

    dashboard: {

        no: {

            tag: "Fase 5 · Bruker", tagClass: "tag-data",

            title: "Bruker dashboard", subtitle: "Oversikt og tilgang for brukeren",

            tooltip: "Bestillinger, kvitteringer og vilkår",

            body: (

                <>

                    <p>Brukerens dashboard er sentralt for selvbetjening. All historikk og dokumentasjon er tilgjengelig her uten behov for kundeservice.</p>

                    <ul className="detail-list">

                        <li>Aktive og tidligere bookinger</li>

                        <li>Alle kvitteringer som PDF</li>

                        <li>Vilkår alltid tilgjengelig under "Terms and conditions"</li>

                        <li>Profilinnstillinger og samtykker</li>

                    </ul>

                </>

            )

        },

        en: {

            tag: "Phase 5 · User", tagClass: "tag-data",

            title: "User dashboard", subtitle: "Overview and access for the user",

            tooltip: "Bookings, receipts, and terms",

            body: (

                <>

                    <p>The user's dashboard is central to self-service. All history and documentation is accessible here without the need for customer support.</p>

                    <ul className="detail-list">

                        <li>Active and previous bookings</li>

                        <li>All receipts as PDF</li>

                        <li>Terms always accessible under "Terms and conditions"</li>

                        <li>Profile settings and consents</li>

                    </ul>

                </>

            )

        }

    },

    accounting: {

        no: {

            tag: "Fase 5 · Regnskap", tagClass: "tag-regnskap",

            title: "Regnskap fra PSP", subtitle: "Direkte tilgang — ikke via plattformen",

            tooltip: "Bedrifter henter rapporter direkte fra PSP-portalen",

            body: (

                <>

                    <p>Bedrifter og agenter henter regnskapsrapporter direkte fra PSP-portalen, ikke fra Chamcore-plattformen.</p>

                    <ul className="detail-list">

                        <li>Komplett transaksjonshistorikk i PSP</li>

                        <li>Eksport til SAF-T og standard regnskapsformater</li>

                        <li>MVA-rapporter generert automatisk</li>

                        <li>Ingen behov for plugin-integrasjoner</li>

                    </ul>

                    <h3 style={{ color: '#27500A' }}>Strategiske konsekvenser</h3>

                    <ul className="detail-list">

                        <li>Lavere teknisk gjeld — ingen integrasjonsvedlikehold</li>

                        <li>Raskere onboarding av nye tilbydere</li>

                        <li>Tydelig ansvarsfordeling mot myndigheter</li>

                        <li>Skalerbar arkitektur uten flaskehalser</li>

                    </ul>

                </>

            )

        },

        en: {

            tag: "Phase 5 · Accounting", tagClass: "tag-regnskap",

            title: "Accounting from PSP", subtitle: "Direct access — not via the platform",

            tooltip: "Companies retrieve reports directly from the PSP portal",

            body: (

                <>

                    <p>Companies and agents retrieve accounting reports directly from the PSP portal, not from the Chamcore platform.</p>

                    <ul className="detail-list">

                        <li>Complete transaction history in the PSP</li>

                        <li>Export to SAF-T and standard accounting formats</li>

                        <li>VAT reports generated automatically</li>

                        <li>No need for plugin integrations</li>

                    </ul>

                    <h3 style={{ color: '#27500A' }}>Strategic consequences</h3>

                    <ul className="detail-list">

                        <li>Lower technical debt — no integration maintenance</li>

                        <li>Faster onboarding of new providers</li>

                        <li>Clear delineation of responsibility toward regulators</li>

                        <li>Scalable architecture without bottlenecks</li>

                    </ul>

                </>

            )

        }

    },

};



// ============================================================ 

// HELPER COMPONENTS 

// ============================================================ 

function MetaRow({ items }) {

    return (

        <div className="meta-row">

            {items.map(([label, value], i) => (

                <div className="meta-item" key={i}>

                    <div className="meta-label">{label}</div>

                    <div className="meta-value">{value}</div>

                </div>

            ))}

        </div>

    );

}



function UnregisteredFlow({ lang }) {

    const t = lang === 'no'

        ? { label: 'Spor A', pill: 'Uregistrert innringer', n1t: 'Anonym', n1s: 'innringer', n2t: 'Vipps push', n2s: 'til mobil', n3t: 'Bekreft i app', n3s: 'biometri / PIN', n4t: 'Verifisert', n4s: 'og betalende', note: 'Én bekreftelse: konto + ID + preauth — innringer legger ikke på' }

        : { label: 'Track A', pill: 'Unregistered caller', n1t: 'Anonymous', n1s: 'caller', n2t: 'Vipps push', n2s: 'to mobile', n3t: 'Confirm in app', n3s: 'biometrics / PIN', n4t: 'Verified', n4s: 'and paying', note: 'One confirmation: account + ID + preauth — caller stays on the line' };

    return (

        <div className="micro-flow">

            <div className="micro-flow-label">{t.label} <span className="pill">{t.pill}</span></div>

            <svg viewBox="0 0 520 110" xmlns="http://www.w3.org/2000/svg">

                <defs>

                    <marker id="ma1" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">

                        <path d="M2 1L8 5L2 9" fill="none" stroke="context-stroke" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />

                    </marker>

                </defs>

                <rect x="6" y="30" width="110" height="50" rx="8" fill="#F1EFE8" stroke="#5F5E5A" strokeWidth="0.5" />

                <text x="61" y="52" textAnchor="middle" fontSize="12" fontWeight="600" fill="#2C2C2A">{t.n1t}</text>

                <text x="61" y="68" textAnchor="middle" fontSize="11" fill="#5F5E5A">{t.n1s}</text>

                <line x1="118" y1="55" x2="138" y2="55" stroke="#5F5E5A" strokeWidth="1.2" markerEnd="url(#ma1)" />

                <rect x="140" y="30" width="110" height="50" rx="8" fill="#FAEEDA" stroke="#854F0B" strokeWidth="0.5" />

                <text x="195" y="52" textAnchor="middle" fontSize="12" fontWeight="600" fill="#633806">{t.n2t}</text>

                <text x="195" y="68" textAnchor="middle" fontSize="11" fill="#854F0B">{t.n2s}</text>

                <line x1="252" y1="55" x2="272" y2="55" stroke="#5F5E5A" strokeWidth="1.2" markerEnd="url(#ma1)" />

                <rect x="274" y="30" width="116" height="50" rx="8" fill="#FAEEDA" stroke="#854F0B" strokeWidth="0.5" />

                <text x="332" y="52" textAnchor="middle" fontSize="12" fontWeight="600" fill="#633806">{t.n3t}</text>

                <text x="332" y="68" textAnchor="middle" fontSize="11" fill="#854F0B">{t.n3s}</text>

                <line x1="392" y1="55" x2="412" y2="55" stroke="#5F5E5A" strokeWidth="1.2" markerEnd="url(#ma1)" />

                <rect x="414" y="30" width="100" height="50" rx="8" fill="#E1F5EE" stroke="#0F6E56" strokeWidth="0.5" />

                <text x="464" y="52" textAnchor="middle" fontSize="12" fontWeight="600" fill="#085041">{t.n4t}</text>

                <text x="464" y="68" textAnchor="middle" fontSize="11" fill="#0F6E56">{t.n4s}</text>

                <text x="6" y="100" fontSize="10" fill="#5F5E5A" fontStyle="italic">{t.note}</text>

            </svg>

        </div>

    );

}



function RegisteredFlow({ lang }) {

    const t = lang === 'no'

        ? { label: 'Spor B', pill: 'Registrert kunde', n1t: 'Ringer inn', n1s: 'gjenkjent', n2t: 'Velg agent', n2s: 'via meny', n3t: 'Velg tjeneste', n3s: 'tid eller fastpris', n4t: 'Vipps push', n4s: 'autorisering', n5t: 'Kobles til agent', n5s: 'samtale starter', n6t: 'Legger på', n6s: 'samtale ferdig', n7t: 'Capture', n7s: 'via PSP', note: 'Hvis agent ikke svarer: full release of hold (instant) — ingen midler trekkes' }

        : { label: 'Track B', pill: 'Registered customer', n1t: 'Calls in', n1s: 'recognized', n2t: 'Pick agent', n2s: 'via menu', n3t: 'Pick service', n3s: 'time or fixed price', n4t: 'Vipps push', n4s: 'authorization', n5t: 'Connected to agent', n5s: 'call begins', n6t: 'Hangs up', n6s: 'call complete', n7t: 'Capture', n7s: 'via PSP', note: 'If agent doesn\'t answer: full release of hold (instant) — no funds charged' };

    return (

        <div className="micro-flow">

            <div className="micro-flow-label">{t.label} <span className="pill">{t.pill}</span></div>

            <svg viewBox="0 0 680 220" xmlns="http://www.w3.org/2000/svg">

                <defs>

                    <marker id="ma2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">

                        <path d="M2 1L8 5L2 9" fill="none" stroke="context-stroke" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />

                    </marker>

                </defs>

                <rect x="6" y="20" width="105" height="50" rx="8" fill="#E6F1FB" stroke="#185FA5" strokeWidth="0.5" />

                <text x="58" y="42" textAnchor="middle" fontSize="12" fontWeight="600" fill="#0C447C">{t.n1t}</text>

                <text x="58" y="58" textAnchor="middle" fontSize="11" fill="#185FA5">{t.n1s}</text>

                <line x1="113" y1="45" x2="133" y2="45" stroke="#5F5E5A" strokeWidth="1.2" markerEnd="url(#ma2)" />

                <rect x="135" y="20" width="105" height="50" rx="8" fill="#EEEDFE" stroke="#534AB7" strokeWidth="0.5" />

                <text x="187" y="42" textAnchor="middle" fontSize="12" fontWeight="600" fill="#3C3489">{t.n2t}</text>

                <text x="187" y="58" textAnchor="middle" fontSize="11" fill="#534AB7">{t.n2s}</text>

                <line x1="242" y1="45" x2="262" y2="45" stroke="#5F5E5A" strokeWidth="1.2" markerEnd="url(#ma2)" />

                <rect x="264" y="20" width="120" height="50" rx="8" fill="#EEEDFE" stroke="#534AB7" strokeWidth="0.5" />

                <text x="324" y="42" textAnchor="middle" fontSize="12" fontWeight="600" fill="#3C3489">{t.n3t}</text>

                <text x="324" y="58" textAnchor="middle" fontSize="11" fill="#534AB7">{t.n3s}</text>

                <line x1="386" y1="45" x2="406" y2="45" stroke="#5F5E5A" strokeWidth="1.2" markerEnd="url(#ma2)" />

                <rect x="408" y="20" width="115" height="50" rx="8" fill="#FAEEDA" stroke="#854F0B" strokeWidth="0.5" />

                <text x="465" y="42" textAnchor="middle" fontSize="12" fontWeight="600" fill="#633806">{t.n4t}</text>

                <text x="465" y="58" textAnchor="middle" fontSize="11" fill="#854F0B">{t.n4s}</text>

                <line x1="525" y1="45" x2="545" y2="45" stroke="#5F5E5A" strokeWidth="1.2" markerEnd="url(#ma2)" />

                <rect x="547" y="20" width="125" height="50" rx="8" fill="#E1F5EE" stroke="#0F6E56" strokeWidth="0.5" />

                <text x="609" y="42" textAnchor="middle" fontSize="12" fontWeight="600" fill="#085041">{t.n5t}</text>

                <text x="609" y="58" textAnchor="middle" fontSize="11" fill="#0F6E56">{t.n5s}</text>



                <path d="M 609 70 L 609 95 L 280 95 L 280 120" fill="none" stroke="#5F5E5A" strokeWidth="1.2" markerEnd="url(#ma2)" />



                <rect x="220" y="120" width="120" height="50" rx="8" fill="#FAECE7" stroke="#993C1D" strokeWidth="0.5" />

                <text x="280" y="142" textAnchor="middle" fontSize="12" fontWeight="600" fill="#712B13">{t.n6t}</text>

                <text x="280" y="158" textAnchor="middle" fontSize="11" fill="#993C1D">{t.n6s}</text>

                <line x1="342" y1="145" x2="362" y2="145" stroke="#5F5E5A" strokeWidth="1.2" markerEnd="url(#ma2)" />

                <rect x="364" y="120" width="115" height="50" rx="8" fill="#FAECE7" stroke="#993C1D" strokeWidth="0.5" />

                <text x="421" y="142" textAnchor="middle" fontSize="12" fontWeight="600" fill="#712B13">{t.n7t}</text>

                <text x="421" y="158" textAnchor="middle" fontSize="11" fill="#993C1D">{t.n7s}</text>



                <text x="6" y="200" fontSize="10" fill="#5F5E5A" fontStyle="italic">{t.note}</text>

            </svg>

        </div>

    );

}



// ============================================================ 

// FlowNode — interactive node component 

// ============================================================ 

function FlowNode({ x, y, w, h, fill, stroke, textFill, subFill, title, subtitle, subtitle2, onClick }) {

    return (

        <g

            style={{ cursor: 'pointer' }}

            onClick={(e) => { e.stopPropagation(); onClick(); }}

            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}

            tabIndex={0}

            role="button"

            className="flow-node"

        >

            <rect x={x} y={y} width={w} height={h} rx="8" fill={fill} stroke={stroke} strokeWidth="0.5" />

            <text x={x + w / 2} y={y + (subtitle ? h / 2 - 6 : h / 2 + 5)} textAnchor="middle" fontSize="15" fontWeight="500" fill={textFill}>{title}</text>

            {subtitle && <text x={x + w / 2} y={y + (subtitle2 ? h / 2 + 8 : h / 2 + 14)} textAnchor="middle" fontSize="13" fill={subFill}>{subtitle}</text>}

            {subtitle2 && <text x={x + w / 2} y={y + h / 2 + 24} textAnchor="middle" fontSize="13" fill={subFill}>{subtitle2}</text>}

        </g>

    );

}



// ============================================================ 

// MAIN COMPONENT 

// ============================================================ 
type FlowPageProps = {
    baseUrl: string,
    currentUser: any,
    onLoginClick: any,
    onLoginSuccess: any,
    onLogout: any,
}

export default function FlowPage({ baseUrl, currentUser, onLoginClick, onLoginSuccess, onLogout }: FlowPageProps) {
  const T = useTranslations("home");
    const client = useSelector((state: RootState) => state.client.client);
    const [lang, setLang] = useState('no');

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile(); // initial check

        window.addEventListener("resize", checkMobile);

        return () => {
            window.removeEventListener("resize", checkMobile);
        };
    }, []);

    const [activeStep, setActiveStep] = useState(null);



    const t = ui[lang];

    const labels = nodeLabels[lang];



    const closeStep = useCallback(() => setActiveStep(null), []);



    // ESC to close 

    useEffect(() => {

        const onKey = (e) => { if (e.key === 'Escape') closeStep(); };

        window.addEventListener('keydown', onKey);

        return () => window.removeEventListener('keydown', onKey);

    }, [closeStep]);



    // Lock body scroll when modal open 

    useEffect(() => {

        if (activeStep) {

            document.body.style.overflow = 'hidden';

            return () => { document.body.style.overflow = ''; };

        }

    }, [activeStep]);



    const open = activeStep ? stepData[activeStep][lang] : null;

    const theme_settings = client ? JSON.parse(client.theme_settings) : {};

    const primaryColor = theme_settings.primaryColor || "#7c3aed"; // default purple-700
    const secondaryColor = theme_settings.secondaryColor || "#ec4899";

    const [appearance, setAppearance] = useState({
        gradientEnabled: false,
        gradientFrom: `${primaryColor}`,
        gradientTo: `${secondaryColor}`,
    });

    useEffect(() => {
        setAppearance(prev => ({
            gradientFrom: theme_settings.primaryColor,
            gradientTo: theme_settings.secondaryColor,
            gradientEnabled: theme_settings.useGradient === "true",
        }));
    }, [theme_settings.useGradient]);

    const [scrolled, setScrolled] = useState(false);
    const [wallet, setWallet] = useState(0);

    useEffect(() => {
        if (currentUser?.role === "customer") {
            axios.get(baseUrl + "/getCustomerWallet", { params: { token: currentUser.token } })

                .then((response) => {
                    if (response.data.status === "OK") {
                        setWallet(response.data.data.remaining_amount);
                    }
                });
        }
    }, [currentUser, baseUrl])
    const onBack = () => {
        window.history.back();
    }

    return (
        <div style={{
            "--primary-color": primaryColor,
            "--secondary-color": secondaryColor,
        }} className={"bg-white text-gray-900"}>
            <Header wallet={wallet} scrolled={scrolled} T={T} onLoginClick={onLoginClick} currentUser={currentUser} onLogout={onLogout} baseUrl={baseUrl} />
            <div className="chamcore-app">

                <style>{` 

        .chamcore-app { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; background: #FAFAF7; color: #2C2C2A; min-height: 100vh; padding: 32px 24px 80px; -webkit-font-smoothing: antialiased; } 

        .chamcore-inner { max-width: 1280px; margin: 0 auto; } 

        .chamcore-header { display: flex; align-items: center; justify-content: space-between; padding-bottom: 20px; margin-bottom: 28px; border-bottom: 1px solid #E5E3DA; flex-wrap: wrap; gap: 16px; } 

        .logo-svg { height: 48px; width: auto; } 

        .header-right { display: flex; align-items: center; gap: 16px; } 

        .doc-meta { text-align: right; font-size: 12px; color: #5F5E5A; line-height: 1.4; } 

        .doc-meta strong { display: block; color: #2C2C2A; font-size: 13px; font-weight: 600; margin-bottom: 2px; } 

        .lang-toggle { display: inline-flex; background: #F1EFE8; border-radius: 999px; padding: 3px; border: 1px solid #E5E3DA; } 

        .lang-toggle button { background: transparent; border: 0; padding: 6px 14px; border-radius: 999px; font-size: 12.5px; font-weight: 500; color: #5F5E5A; cursor: pointer; transition: all 0.15s ease; font-family: inherit; } 

        .lang-toggle button.active { background: #2C2C2A; color: #fff; } 

        .lang-toggle button:not(.active):hover { color: #2C2C2A; } 

        .chamcore-app h1 { font-size: 28px; font-weight: 600; margin: 0 0 6px; letter-spacing: -0.4px; } 

        .chamcore-app .subtitle { color: #5F5E5A; font-size: 15px; margin: 0 0 8px; } 

        .hint { display: inline-flex; align-items: center; gap: 6px; background: #FAEEDA; border: 1px solid #F0D9A8; color: #633806; font-size: 12px; padding: 5px 10px; border-radius: 999px; margin-top: 10px; } 

        .hint::before { content: "ⓘ"; font-size: 13px; } 

        .legend { display: flex; gap: 18px; flex-wrap: wrap; margin: 18px 0 24px; font-size: 12.5px; color: #5F5E5A; } 

        .legend-item { display: flex; align-items: center; gap: 7px; } 

        .legend-dot { width: 11px; height: 11px; border-radius: 3px; } 

        .flow-card { background: #fff; border: 1px solid #E5E3DA; border-radius: 16px; padding: 24px 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.02); overflow-x: auto; } 

        .flow-svg { width: 100%; min-width: 720px; height: auto; display: block; } 

        .flow-node { transition: filter 0.15s ease; } 

        .flow-node:hover { filter: brightness(0.96) drop-shadow(0 4px 8px rgba(0,0,0,0.08)); } 

        .flow-node:hover rect { stroke-width: 1.5; } 

        .flow-node:focus { outline: none; } 

        .flow-node:focus rect { stroke-width: 2; } 

        .phase-label { font-size: 12px; fill: #5F5E5A; font-weight: 600; letter-spacing: 0.5px; paint-order: stroke fill; stroke: #FFFFFF; stroke-width: 4px; stroke-linejoin: round; } 

        .doc-footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #E5E3DA; display: flex; justify-content: space-between; font-size: 12px; color: #888780; flex-wrap: wrap; gap: 8px; } 

        .overlay { position: fixed; inset: 0; background: rgba(20,20,18,0.4); backdrop-filter: blur(2px); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 24px; animation: fadeIn 0.2s ease; } 

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } } 

        .panel { background: #fff; border-radius: 16px; max-width: 540px; width: 100%; max-height: 85vh; overflow-y: auto; box-shadow: 0 20px 50px rgba(0,0,0,0.18); animation: slideUp 0.25s cubic-bezier(0.2,0.9,0.3,1); } 

        @keyframes slideUp { from { transform: translateY(8px) scale(0.98); } to { transform: translateY(0) scale(1); } } 

        .panel-header { padding: 20px 24px 16px; border-bottom: 1px solid #E5E3DA; position: relative; } 

        .panel-tag { display: inline-block; font-size: 11px; font-weight: 600; letter-spacing: 0.6px; text-transform: uppercase; padding: 3px 8px; border-radius: 4px; margin-bottom: 8px; } 

        .panel-title { font-size: 20px; font-weight: 600; margin: 0; padding-right: 32px; } 

        .panel-subtitle { font-size: 13px; color: #5F5E5A; margin: 4px 0 0; } 

        .panel-close { position: absolute; top: 16px; right: 16px; background: transparent; border: 0; width: 32px; height: 32px; border-radius: 8px; cursor: pointer; color: #5F5E5A; display: flex; align-items: center; justify-content: center; transition: background 0.15s; } 

        .panel-close:hover { background: #F1EFE8; color: #2C2C2A; } 

        .panel-body { padding: 18px 24px 24px; line-height: 1.65; } 

        .panel-body p { margin: 0 0 12px; } 

        .panel-body p:last-child { margin-bottom: 0; } 

        .panel-body h3 { font-size: 14px; margin: 16px 0 8px; font-weight: 600; } 

        .detail-list { list-style: none; padding: 0; margin: 12px 0; } 

        .detail-list li { position: relative; padding: 6px 0 6px 22px; font-size: 14px; border-bottom: 1px solid #F1EFE8; } 

        .detail-list li:last-child { border-bottom: 0; } 

        .detail-list li::before { content: "→"; position: absolute; left: 0; color: #EF9F27; font-weight: 600; } 

        .meta-row { display: flex; gap: 12px; margin-top: 16px; padding-top: 16px; border-top: 1px solid #E5E3DA; flex-wrap: wrap; } 

        .meta-item { flex: 1; min-width: 120px; font-size: 12px; } 

        .meta-label { color: #888780; text-transform: uppercase; letter-spacing: 0.4px; font-size: 10.5px; font-weight: 600; margin-bottom: 3px; } 

        .meta-value { color: #2C2C2A; font-weight: 500; } 

        .tag-discovery { background: #F1EFE8; color: #444441; } 

        .tag-utforsking { background: #EEEDFE; color: #3C3489; } 

        .tag-booking { background: #E1F5EE; color: #085041; } 

        .tag-verifisering { background: #FAEEDA; color: #633806; } 

        .tag-gjennomforing { background: #FAECE7; color: #712B13; } 

        .tag-compliance { background: #FBEAF0; color: #72243E; } 

        .tag-data { background: #E6F1FB; color: #0C447C; } 

        .tag-regnskap { background: #EAF3DE; color: #27500A; } 

        .micro-flow { margin: 12px -4px 16px; padding: 14px 12px 10px; background: #FAFAF7; border: 1px solid #E5E3DA; border-radius: 10px; } 

        .micro-flow-label { font-size: 11px; font-weight: 600; letter-spacing: 0.5px; color: #5F5E5A; text-transform: uppercase; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; } 

        .micro-flow-label .pill { display: inline-block; background: #fff; color: #444441; padding: 1px 7px; border-radius: 999px; border: 1px solid #E5E3DA; text-transform: none; letter-spacing: 0.2px; font-weight: 500; } 

        .micro-flow svg { width: 100%; height: auto; display: block; } 

        .chamcore-mobile { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; background: #FAFAF7; color: #2C2C2A; min-height: 100vh; padding: 18px 0 60px; -webkit-font-smoothing: antialiased; }
        .m-inner { max-width: 480px; margin: 0 auto; padding: 0 16px; }
        .m-header { display: flex; align-items: center; justify-content: space-between; padding-bottom: 16px; margin-bottom: 18px; border-bottom: 1px solid #E5E3DA; gap: 12px; }
        .m-logo { height: 34px; width: auto; flex-shrink: 1; min-width: 0; max-width: 60%; }
        .m-header-controls { flex-shrink: 0; }
        .lang-toggle { display: inline-flex; background: #F1EFE8; border-radius: 999px; padding: 3px; border: 1px solid #E5E3DA; }
        .lang-toggle button { background: transparent; border: 0; padding: 5px 10px; border-radius: 999px; font-size: 12px; font-weight: 500; color: #5F5E5A; cursor: pointer; transition: all 0.15s ease; font-family: inherit; }
        .lang-toggle button.active { background: #2C2C2A; color: #fff; }
        .m-title-block { margin-bottom: 28px; }
        .chamcore-mobile h1 { font-size: 22px; font-weight: 600; margin: 0 0 4px; letter-spacing: -0.3px; line-height: 1.2; }
        .m-subtitle { color: #5F5E5A; font-size: 13px; margin: 0 0 8px; line-height: 1.4; }
        .m-hint { display: inline-flex; align-items: center; gap: 6px; background: #FAEEDA; border: 1px solid #F0D9A8; color: #633806; font-size: 11.5px; padding: 4px 10px; border-radius: 999px; }
        .m-hint::before { content: "ⓘ"; font-size: 12px; }
        .m-flowchart { display: flex; flex-direction: column; align-items: center; }
        .m-phase-marker { display: flex; align-items: center; gap: 8px; width: 100%; margin: 18px 0 12px; }
        .m-phase-marker:first-child { margin-top: 0; }
        .m-phase-dot { width: 12px; height: 12px; border-radius: 4px; flex-shrink: 0; }
        .m-phase-text { font-size: 10.5px; font-weight: 600; letter-spacing: 0.5px; color: #5F5E5A; white-space: nowrap; flex-shrink: 0; }
        .m-phase-line { flex: 1; height: 1px; background: #E5E3DA; }
        .m-node { display: flex; align-items: stretch; gap: 0; width: 100%; max-width: 320px; padding: 0; border-radius: 10px; border: 1px solid; background: #fff; cursor: pointer; text-align: left; font-family: inherit; transition: transform 0.1s ease, box-shadow 0.15s ease; overflow: hidden; }
        .m-node:hover, .m-node:focus { box-shadow: 0 3px 10px rgba(0,0,0,0.06); outline: none; transform: translateY(-1px); }
        .m-node:active { transform: scale(0.985); }
        .m-node-bar { width: 4px; flex-shrink: 0; }
        .m-node-content { flex: 1; padding: 12px 14px; display: flex; flex-direction: column; gap: 2px; }
        .m-node-title { font-size: 14px; font-weight: 600; line-height: 1.3; }
        .m-node-sub { font-size: 12px; line-height: 1.4; }
        .m-connector { display: flex; justify-content: center; height: 28px; align-items: center; position: relative; width: 100%; }
        .m-connector::after { content: ''; width: 0; height: 0; border-left: 4px solid transparent; border-right: 4px solid transparent; border-top: 5px solid #888780; position: absolute; top: 18px; }
        .m-connector-line { width: 2px; height: 22px; background: #888780; opacity: 0.45; }
        .m-flow-label { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 600; letter-spacing: 0.4px; text-transform: uppercase; color: #5F5E5A; padding: 8px 12px; background: #F1EFE8; border-radius: 999px; margin: 6px 0; }
        .m-branch { display: flex; flex-direction: column; gap: 8px; width: 100%; max-width: 320px; position: relative; padding-left: 18px; }
        .m-branch::before { content: ''; position: absolute; left: 8px; top: 8px; bottom: 8px; width: 2px; background: #888780; opacity: 0.3; border-radius: 1px; }
        .m-branch-node { display: flex; flex-direction: column; gap: 2px; padding: 11px 14px; border-radius: 10px; border: 1px solid; cursor: pointer; text-align: left; font-family: inherit; transition: transform 0.1s ease, box-shadow 0.15s ease; position: relative; }
        .m-branch-node::before { content: ''; position: absolute; left: -10px; top: 50%; width: 10px; height: 2px; background: #888780; opacity: 0.3; }
        .m-branch-node:hover, .m-branch-node:focus { box-shadow: 0 2px 6px rgba(0,0,0,0.05); outline: none; }
        .m-branch-node:active { transform: scale(0.98); }
        .m-branch-title { font-size: 13.5px; font-weight: 600; line-height: 1.3; }
        .m-branch-sub { font-size: 11.5px; line-height: 1.4; }
        .m-parallel { display: flex; gap: 8px; width: 100%; max-width: 340px; }
        .m-parallel-node { flex: 1; display: flex; flex-direction: column; gap: 2px; padding: 11px 12px; border-radius: 10px; border: 1px solid; cursor: pointer; text-align: left; font-family: inherit; transition: transform 0.1s ease, box-shadow 0.15s ease; min-width: 0; }
        .m-parallel-node:hover, .m-parallel-node:focus { box-shadow: 0 2px 6px rgba(0,0,0,0.05); outline: none; }
        .m-parallel-node:active { transform: scale(0.98); }
        .m-parallel-title { font-size: 13px; font-weight: 600; line-height: 1.25; }
        .m-parallel-sub { font-size: 11px; line-height: 1.35; }
        .m-merge { display: flex; align-items: center; gap: 8px; width: 100%; max-width: 320px; margin: 6px 0; }
        .m-merge-line { flex: 1; height: 1px; background: #888780; opacity: 0.3; }
        .m-merge-line-left { background: linear-gradient(to right, #888780 0%, #888780 60%, transparent 100%); opacity: 0.4; }
        .m-merge-line-right { background: linear-gradient(to left, #888780 0%, #888780 60%, transparent 100%); opacity: 0.4; }
        .m-merge-text { font-size: 10px; font-weight: 600; letter-spacing: 0.4px; text-transform: uppercase; color: #5F5E5A; white-space: nowrap; }
        .m-footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #E5E3DA; display: flex; flex-direction: column; gap: 4px; font-size: 11px; color: #888780; }

        @media (max-width: 640px) { .chamcore-app { padding: 20px 14px 60px; } .chamcore-app h1 { font-size: 22px; } .header-right { width: 100%; justify-content: space-between; } .legend { gap: 12px; } } 

      `}</style>



                <div className="chamcore-inner">



                    {/* Header */}

                    <div className="chamcore-header">

                        <ChamcoreLogoSVG />

                        <div className="header-right">

                            <div className="lang-toggle" role="group" aria-label="Language">

                                <button className={lang === 'no' ? 'active' : ''} aria-pressed={lang === 'no'} onClick={() => setLang('no')}>🇳🇴 NO</button>

                                <button className={lang === 'en' ? 'active' : ''} aria-pressed={lang === 'en'} onClick={() => setLang('en')}>🇬🇧 EN</button>

                            </div>

                            <div className="doc-meta">

                                <strong>{t.metaTitle}</strong>

                                {t.metaSub}

                            </div>

                        </div>

                    </div>

                    <button
                        onClick={onBack}
                        className="mb-6 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                        ← Tilbake
                    </button>

                    <h1>{t.pageTitle}</h1>

                    <p className="subtitle">{t.pageSubtitle}</p>

                    <div className="hint">{t.hint}</div>



                    {/* Legend */}

                    <div className="legend">

                        {[

                            ['#D3D1C7', t.legend[0]],

                            ['#CECBF6', t.legend[1]],

                            ['#9FE1CB', t.legend[2]],

                            ['#FAC775', t.legend[3]],

                            ['#F5C4B3', t.legend[4]],

                            ['#F4C0D1', t.legend[5]],

                            ['#C0DD97', t.legend[6]],

                        ].map(([color, label]) => (

                            <span className="legend-item" key={label}>

                                <span className="legend-dot" style={{ background: color }}></span>{label}

                            </span>

                        ))}

                    </div>



                    {/* Flow diagram */}

                    <div className="flow-card">

                        {!isMobile && (
                            <FlowDiagram t={t} labels={labels} onOpen={setActiveStep} />
                        )}

                        {isMobile && (
                            <MobileFlowDiagram t={t} labels={labels} onOpen={setActiveStep} lang={lang} />
                        )}

                    </div>



                    <div className="doc-footer">

                        <span>{t.footerLeft}</span>

                        <span>{t.footerRight}</span>

                    </div>

                </div>



                {/* Modal */}

                {open && (

                    <div className="overlay" onClick={(e) => { if (e.target === e.currentTarget) closeStep(); }} role="dialog" aria-modal="true">

                        <div className="panel">

                            <div className="panel-header">

                                <span className={`panel-tag ${open.tagClass}`}>{open.tag}</span>

                                <h2 className="panel-title">{open.title}</h2>

                                <p className="panel-subtitle">{open.subtitle}</p>

                                <button className="panel-close" onClick={closeStep} aria-label={t.closeAria}>

                                    <X size={16} strokeWidth={1.5} />

                                </button>

                            </div>

                            <div className="panel-body">{open.body}</div>

                        </div>

                    </div>

                )}

            </div>
            <Footer baseUrl={baseUrl} />
        </div>

    );

}



// ============================================================ 

// LOGO 

// ============================================================ 

function ChamcoreLogoSVG() {

    return (

        <svg className="logo-svg" viewBox="0 0 480 130" xmlns="http://www.w3.org/2000/svg" aria-label="Chamcore Technologies">

            <g>

                <line x1="22" y1="48" x2="55" y2="35" stroke="#EF9F27" strokeWidth="2.5" />

                <line x1="22" y1="48" x2="48" y2="78" stroke="#EF9F27" strokeWidth="2.5" />

                <line x1="55" y1="35" x2="92" y2="48" stroke="#EF9F27" strokeWidth="2.5" />

                <line x1="55" y1="35" x2="78" y2="20" stroke="#EF9F27" strokeWidth="2.5" />

                <line x1="92" y1="48" x2="78" y2="20" stroke="#EF9F27" strokeWidth="2.5" />

                <line x1="92" y1="48" x2="125" y2="38" stroke="#EF9F27" strokeWidth="2.5" />

                <line x1="92" y1="48" x2="48" y2="78" stroke="#EF9F27" strokeWidth="2.5" />

                <line x1="48" y1="78" x2="82" y2="92" stroke="#EF9F27" strokeWidth="2.5" />

                <line x1="125" y1="38" x2="78" y2="20" stroke="#EF9F27" strokeWidth="2.5" />

                <circle cx="78" cy="20" r="8" fill="#F2A623" />

                <circle cx="22" cy="48" r="9" fill="#F2A623" />

                <circle cx="55" cy="35" r="10" fill="#EF7B23" />

                <circle cx="92" cy="48" r="13" fill="#E85D24" />

                <circle cx="125" cy="38" r="7" fill="#F2A623" />

                <circle cx="48" cy="78" r="11" fill="#F2A623" />

                <circle cx="82" cy="92" r="8" fill="#EF7B23" />

            </g>

            <text x="160" y="60" fontFamily="Arial, Helvetica, sans-serif" fontSize="44" fontWeight="700" fill="#5F5E5A" letterSpacing="2">CHAMCORE</text>

            <text x="162" y="92" fontFamily="Arial, Helvetica, sans-serif" fontSize="20" fontWeight="600" fill="#EF9F27" letterSpacing="6">TECHNOLOGIES</text>

        </svg>

    );

}



// ============================================================ 

// FLOW DIAGRAM 

// ============================================================ 

function FlowDiagram({ t, labels, onOpen }) {

    return (

        <svg className="flow-svg" viewBox="0 -10 800 1190" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Chamcore marketplace flow diagram">

            <defs>

                <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">

                    <path d="M2 1L8 5L2 9" fill="none" stroke="context-stroke" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />

                </marker>

            </defs>



            {/* PHASE 1 */}

            <rect x="20" y="20" width="760" height="160" rx="14" fill="none" strokeDasharray="4 4" stroke="#888780" strokeWidth="0.5" opacity="0.5" />

            <text x="36" y="14" className="phase-label">{t.phase1}</text>



            <FlowNode x={80} y={60} w={200} h={60} fill="#F1EFE8" stroke="#5F5E5A" textFill="#2C2C2A" subFill="#444441" title={labels.landing[0]} subtitle={labels.landing[1]} onClick={() => onOpen('landing')} />

            <line x1="280" y1="90" x2="304" y2="90" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />

            <FlowNode x={310} y={60} w={200} h={60} fill="#E6F1FB" stroke="#185FA5" textFill="#0C447C" subFill="#185FA5" title={labels.search[0]} subtitle={labels.search[1]} onClick={() => onOpen('search')} />

            <line x1="510" y1="90" x2="534" y2="90" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />

            <FlowNode x={540} y={60} w={180} h={60} fill="#E6F1FB" stroke="#185FA5" textFill="#0C447C" subFill="#185FA5" title={labels.results[0]} subtitle={labels.results[1]} onClick={() => onOpen('results')} />



            <path d="M 630 120 L 630 150 L 180 150 L 180 196" fill="none" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />



            {/* PHASE 2 */}

            <rect x="20" y="200" width="760" height="200" rx="14" fill="none" strokeDasharray="4 4" stroke="#888780" strokeWidth="0.5" opacity="0.5" />

            <text x="36" y="194" className="phase-label">{t.phase2}</text>



            <FlowNode x={80} y={240} w={200} h={60} fill="#EEEDFE" stroke="#534AB7" textFill="#3C3489" subFill="#534AB7" title={labels.company[0]} subtitle={labels.company[1]} onClick={() => onOpen('company')} />



            <path d="M 180 300 L 180 326 L 410 326 L 410 354" fill="none" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />

            <path d="M 180 326 L 630 326 L 630 354" fill="none" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />

            <line x1="180" y1="326" x2="180" y2="354" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />



            <FlowNode x={80} y={358} w={200} h={40} fill="#EEEDFE" stroke="#534AB7" textFill="#3C3489" subFill="#534AB7" title={labels.agents[0]} onClick={() => onOpen('agents')} />

            <FlowNode x={310} y={358} w={200} h={40} fill="#EEEDFE" stroke="#534AB7" textFill="#3C3489" subFill="#534AB7" title={labels.blog[0]} onClick={() => onOpen('blog')} />

            <FlowNode x={540} y={358} w={180} h={40} fill="#EEEDFE" stroke="#534AB7" textFill="#3C3489" subFill="#534AB7" title={labels.bookingStart[0]} onClick={() => onOpen('bookingStart')} />



            <line x1="630" y1="398" x2="630" y2="436" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />



            {/* PHASE 3 */}

            <rect x="20" y="425" width="760" height="220" rx="14" fill="none" strokeDasharray="4 4" stroke="#888780" strokeWidth="0.5" opacity="0.5" />

            <text x="36" y="419" className="phase-label">{t.phase3}</text>



            <FlowNode x={540} y={440} w={180} h={60} fill="#E1F5EE" stroke="#0F6E56" textFill="#085041" subFill="#0F6E56" title={labels.slot[0]} subtitle={labels.slot[1]} onClick={() => onOpen('slot')} />

            <line x1="540" y1="470" x2="516" y2="470" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />

            <FlowNode x={310} y={440} w={200} h={60} fill="#E1F5EE" stroke="#0F6E56" textFill="#085041" subFill="#0F6E56" title={labels.terms[0]} subtitle={labels.terms[1]} onClick={() => onOpen('terms')} />

            <line x1="310" y1="470" x2="286" y2="470" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />

            <FlowNode x={80} y={440} w={200} h={60} fill="#E1F5EE" stroke="#0F6E56" textFill="#085041" subFill="#0F6E56" title={labels.preauth[0]} subtitle={labels.preauth[1]} onClick={() => onOpen('preauth')} />



            <line x1="180" y1="500" x2="180" y2="526" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />



            <FlowNode x={80} y={530} w={200} h={60} fill="#E1F5EE" stroke="#0F6E56" textFill="#085041" subFill="#0F6E56" title={labels.email[0]} subtitle={labels.email[1]} onClick={() => onOpen('email')} />

            <line x1="280" y1="560" x2="304" y2="560" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />

            <FlowNode x={310} y={530} w={200} h={60} fill="#FAEEDA" stroke="#854F0B" textFill="#633806" subFill="#854F0B" title={labels.account[0]} subtitle={labels.account[1]} onClick={() => onOpen('account')} />

            <line x1="510" y1="560" x2="534" y2="560" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />

            <FlowNode x={540} y={530} w={180} h={60} fill="#FAEEDA" stroke="#854F0B" textFill="#633806" subFill="#854F0B" title={labels.bankid[0]} subtitle={labels.bankid[1]} onClick={() => onOpen('bankid')} />



            <line x1="630" y1="590" x2="630" y2="666" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />



            {/* PHASE 4 */}

            <rect x="20" y="660" width="760" height="120" rx="14" fill="none" strokeDasharray="4 4" stroke="#888780" strokeWidth="0.5" opacity="0.5" />

            <text x="36" y="654" className="phase-label">{t.phase4}</text>



            <FlowNode x={540} y={700} w={180} h={60} fill="#FAECE7" stroke="#993C1D" textFill="#712B13" subFill="#993C1D" title={labels.consultation[0]} subtitle={labels.consultation[1]} onClick={() => onOpen('consultation')} />

            <line x1="540" y1="730" x2="516" y2="730" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />

            <FlowNode x={310} y={700} w={200} h={60} fill="#FAECE7" stroke="#993C1D" textFill="#712B13" subFill="#993C1D" title={labels.capture[0]} subtitle={labels.capture[1]} onClick={() => onOpen('capture')} />

            <line x1="310" y1="730" x2="286" y2="730" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />

            <FlowNode x={80} y={700} w={200} h={60} fill="#FAECE7" stroke="#993C1D" textFill="#712B13" subFill="#993C1D" title={labels.receipt[0]} subtitle={labels.receipt[1]} onClick={() => onOpen('receipt')} />



            <path d="M 180 760 L 180 800 L 410 800 L 410 866" fill="none" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />



            {/* PHASE 5 */}

            <rect x="20" y="855" width="760" height="305" rx="14" fill="none" strokeDasharray="4 4" stroke="#888780" strokeWidth="0.5" opacity="0.5" />

            <text x="36" y="849" className="phase-label">{t.phase5}</text>



            <FlowNode x={310} y={870} w={200} h={60} fill="#F1EFE8" stroke="#5F5E5A" textFill="#2C2C2A" subFill="#444441" title={labels.platform[0]} subtitle={labels.platform[1]} onClick={() => onOpen('platform')} />



            <path d="M 310 900 L 240 900 L 240 950" fill="none" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />

            <path d="M 510 900 L 580 900 L 580 950" fill="none" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />



            <FlowNode x={60} y={954} w={360} h={76} fill="#FBEAF0" stroke="#993556" textFill="#72243E" subFill="#993556" title={labels.psp[0]} subtitle={labels.psp[1]} subtitle2={labels.psp[2]} onClick={() => onOpen('psp')} />

            <FlowNode x={460} y={954} w={280} h={76} fill="#E6F1FB" stroke="#185FA5" textFill="#0C447C" subFill="#185FA5" title={labels.dashboard[0]} subtitle={labels.dashboard[1]} subtitle2={labels.dashboard[2]} onClick={() => onOpen('dashboard')} />



            <path d="M 240 1030 L 240 1064 L 600 1064 L 600 1030" fill="none" stroke="#888780" strokeWidth="0.5" strokeDasharray="3 3" />



            <FlowNode x={220} y={1080} w={360} h={50} fill="#EAF3DE" stroke="#3B6D11" textFill="#27500A" subFill="#3B6D11" title={labels.accounting[0]} subtitle={labels.accounting[1]} onClick={() => onOpen('accounting')} />



            <line x1="400" y1="1064" x2="400" y2="1076" stroke="#5F5E5A" strokeWidth="1.5" markerEnd="url(#arrow)" />

        </svg>

    );

}

function MobileFlowDiagram({ t, labels, onOpen, lang }) {
    // const [activeStep, setActiveStep] = useState(null);
    // const closeStep = useCallback(() => setActiveStep(null), []);

    // useEffect(() => {
    //     const onKey = (e) => { if (e.key === 'Escape') closeStep(); };
    //     window.addEventListener('keydown', onKey);
    //     return () => window.removeEventListener('keydown', onKey);
    // }, [closeStep]);

    // useEffect(() => {
    //     if (activeStep) {
    //     const prev = document.body.style.overflow;
    //     document.body.style.overflow = 'hidden';
    //     return () => { document.body.style.overflow = prev; };
    //     }
    // }, [activeStep]);

    // const open = activeStep ? stepData[activeStep][lang] : null;
    return (
        <div className="m-flowchart">
            {flowStructure.map((item, idx) => {
                if (item.type === 'phase') {
                    return (
                        <div className="m-phase-marker" key={`phase-${idx}`}>
                            <span className="m-phase-dot" style={{ background: item.color }}></span>
                            <span className="m-phase-text">{t[item.key]}</span>
                            <span className="m-phase-line"></span>
                        </div>
                    );
                }
                if (item.type === 'step') {
                    const colors = stepColors[item.step];
                    const label = labels[item.step];
                    return (
                        <button
                            key={`step-${idx}-${item.step}`}
                            className="m-node"
                            style={{ background: colors.bg, borderColor: colors.border + '50' }}
                            onClick={() => onOpen(item.step)}
                        >
                            <span className="m-node-bar" style={{ background: colors.accent }}></span>
                            <span className="m-node-content">
                                <span className="m-node-title" style={{ color: colors.text }}>{label[0]}</span>
                                {label[1] && <span className="m-node-sub" style={{ color: colors.sub }}>{label[1]}</span>}
                            </span>
                        </button>
                    );
                }
                if (item.type === 'arrow') {
                    return <div className="m-connector" key={`arr-${idx}`}><span className="m-connector-line"></span></div>;
                }
                if (item.type === 'branchLabel') {
                    return (
                        <div className="m-flow-label" key={`bl-${idx}`}>
                            <GitBranch size={13} />
                            <span>{t.branchLabel}</span>
                        </div>
                    );
                }
                if (item.type === 'splitLabel') {
                    return (
                        <div className="m-flow-label" key={`sl-${idx}`}>
                            <GitBranch size={13} />
                            <span>{t.splitLabel}</span>
                        </div>
                    );
                }
                if (item.type === 'mergeLabel') {
                    return (
                        <div className="m-merge" key={`ml-${idx}`}>
                            <span className="m-merge-line m-merge-line-left"></span>
                            <span className="m-merge-text">{t.mergeLabel}</span>
                            <span className="m-merge-line m-merge-line-right"></span>
                        </div>
                    );
                }
                if (item.type === 'branch') {
                    return (
                        <div className="m-branch" key={`branch-${idx}`}>
                            {item.steps.map(stepKey => {
                                const colors = stepColors[stepKey];
                                const label = labels[stepKey];
                                return (
                                    <button
                                        key={stepKey}
                                        className="m-branch-node"
                                        style={{ background: colors.bg, borderColor: colors.border + '50' }}
                                        onClick={() => onOpen(stepKey)}
                                    >
                                        <span className="m-branch-title" style={{ color: colors.text }}>{label[0]}</span>
                                        {label[1] && <span className="m-branch-sub" style={{ color: colors.sub }}>{label[1]}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    );
                }
                if (item.type === 'parallel') {
                    return (
                        <div className="m-parallel" key={`par-${idx}`}>
                            {item.steps.map(stepKey => {
                                const colors = stepColors[stepKey];
                                const label = labels[stepKey];
                                return (
                                    <button
                                        key={stepKey}
                                        className="m-parallel-node"
                                        style={{ background: colors.bg, borderColor: colors.border + '50' }}
                                        onClick={() => onOpen(stepKey)}
                                    >
                                        <span className="m-parallel-title" style={{ color: colors.text }}>{label[0]}</span>
                                        {label[1] && <span className="m-parallel-sub" style={{ color: colors.sub }}>{label[1]}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    );
                }
                return null;
            })}
        </div>
    )
}